import { styled, SxProp, useSx } from 'dripsy';
import React, { useMemo } from 'react';
import { View, SectionList } from 'react-native';
import { observer } from 'mobx-react-lite';
import {
  EdgeInsets,
  useSafeAreaInsets,
} from 'react-native-safe-area-context';
import { useNavigation } from '@react-navigation/native';

import { BaseText, Separator } from '@components';
import { PlaylistItem } from './PlaylistItem';
import { Course } from '@models/Courses/CourseModel';
import { Tutorial } from '@models/Tutorials/TutorialModel';
import { HubNavigatorNavigationPropType, screens } from '@navigation';

interface ISectionTitleProps {
  isFirstSectionItem: boolean;
}

const SectionTitle = styled(BaseText)(
  (props: ISectionTitleProps) => ({
    color: '$text400',
    variant: 'text.medium',
    pt: props.isFirstSectionItem ? null : '$8',
    pb: '$5',
  }),
);

interface ICoursePlaylistProps {
  ListHeaderComponent?:
    | React.ComponentType<any>
    | React.ReactElement
    | null;
  course: Course;
}

interface ISection {
  id: number;
  title: string;
  data: Tutorial[];
}

const Container = styled(View)({
  flex: 1,
  bg: '$background0',
});

function contentContainerStyle(props: EdgeInsets): SxProp {
  return (theme) => ({
    px: ['$6', '$7', '$0'],
    pb: theme.space.$6 + props.bottom,
  });
}

type NavigationPropType = HubNavigatorNavigationPropType<'Tutorial'>;

export const CoursePlaylist = observer(
  ({ ListHeaderComponent, course }: ICoursePlaylistProps) => {
    const insets = useSafeAreaInsets();
    const sx = useSx();
    const nav = useNavigation<NavigationPropType>();
    const sections = useMemo((): ISection[] => {
      return course.lessons.asArray.map((lesson) => ({
        id: lesson.id,
        title: lesson.name,
        data: lesson.tutorials,
      }));
    }, [course.lessons.asArray]);

    function navigateToTutorial(tutorial: Tutorial) {
      nav.replace(screens.Tutorial, {
        id: tutorial.id,
        courseId: course.id,
      });
    }

    return (
      <Container>
        <SectionList
          contentContainerStyle={sx(contentContainerStyle(insets))}
          keyExtractor={(item) => item.id.toString()}
          sections={sections}
          ListHeaderComponent={ListHeaderComponent}
          renderSectionHeader={({ section }) => (
            <SectionTitle
              isFirstSectionItem={section.id === sections[0].id}
            >
              {section.title}
            </SectionTitle>
          )}
          renderItem={({ item }) => (
            <PlaylistItem item={item} onPress={navigateToTutorial} />
          )}
          ItemSeparatorComponent={() => (
            <Separator size={['$6', '$8']} />
          )}
          stickySectionHeadersEnabled={false}
        />
      </Container>
    );
  },
);
